import React, { Component, ErrorInfo, ReactNode } from 'react';
import BrainiacsLoader from './atoms/BrainiacsLoader';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🛑 Error Boundary caught an error:', error);
    console.error('Error Info:', errorInfo);

    // Check if we've already tried reloading
    const reloadAttempts = sessionStorage.getItem('errorBoundaryReloads') || '0';
    const attempts = parseInt(reloadAttempts, 10);

    if (attempts < 1) {
      // Auto-reload once on first error (likely Firestore index building)
      console.log('🔄 Error boundary triggering auto-reload (first attempt)...');
      sessionStorage.setItem('errorBoundaryReloads', String(attempts + 1));

      // Reload after a short delay to show loading state
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      // Clear counter - show error on second attempt
      sessionStorage.removeItem('errorBoundaryReloads');
      console.error('❌ Second error caught, showing error UI');
    }
  }

  render() {
    if (this.state.hasError) {
      // Check if we're auto-reloading (first error)
      const reloadAttempts = sessionStorage.getItem('errorBoundaryReloads') || '0';
      const isAutoReloading = parseInt(reloadAttempts, 10) === 1;

      if (isAutoReloading) {
        // Show branded loader during auto-reload
        return (
          <div className="min-h-screen bg-[#050505]">
            <BrainiacsLoader variant="section" message="Reconnecting..." />
          </div>
        );
      }

      // Show error UI on second error
      return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
          <div className="max-w-md w-full glass-panel rounded-3xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-sm text-gray-400 mb-6">
              The application encountered an unexpected error. Please refresh the page to continue.
            </p>
            <div className="text-xs text-gray-600 mb-6 p-3 bg-red-500/5 rounded-xl text-left overflow-auto max-h-32">
              {this.state.error?.message || 'Unknown error'}
            </div>
            <button
              onClick={() => {
                sessionStorage.removeItem('errorBoundaryReloads');
                window.location.reload();
              }}
              className="w-full px-6 py-3 bg-[#5260FF] text-white rounded-xl hover:bg-[#5260FF]/90 transition-colors font-medium"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
